import { Container } from '@chakra-ui/react';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { Fragment } from 'react';

import { Header } from '@/components/Speaker/Header';
import { LectureList } from '@/components/Speaker/LectureList';
import { SpeakersPageSeo } from '@/components/ui/features/speakers/SpeakersPageSeo/SpeakesPageSeo';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { accessFrom } from '@/constants/custom-request-headers';
import { AccessFrom } from '@/enums/AccessFrom';
import { fetchLectureAccessPermissionsSideload } from '@/queries/lecture-access-permissions';
import { fetchLectureStatistics } from '@/queries/lecture-statistics';
import { queryProfileInstitutions } from '@/queries/profile-instututions';
import { queryProfileStatistics } from '@/queries/profile-statistics';
import { queryLecturesByQueryInfinite } from '@/queries/thin-lectures';
import { queryProfileKeywords, queryThinProfile } from '@/queries/thin-profiles';
import { generateStore } from '@/utils/generateStore';
import { splitIdAndSlug } from '@/utils/helperFunctions';
import { Hydrate } from '@/utils/swr/Hydrate';
import { StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

const SpeakerPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  fallback,
  accessFromLocation,
}) => {
  return (
    <Fragment>
      <Hydrate fallback={fallback}>
        <SpeakersPageSeo />
        <Layout navigationVariant="inverted" accessFrom={accessFromLocation}>
          <Container>
            <Header />
            <LectureList />
          </Container>
        </Layout>
      </Hydrate>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });
  const PAGE_SIZE = 16;

  const { id: userSlug } = urlQueryStringValidate<{ id: string }>(context.query, {
    id: StringParam,
  });

  const { id } = splitIdAndSlug(userSlug);

  try {
    await store.fetchResource(queryThinProfile(id));

    await store.fetchResources(queryProfileKeywords(id));
    await store.fetchResources(queryProfileInstitutions(id));
    await store.fetchResource(queryProfileStatistics(id));

    await store.fetchResourcesInfinite(
      queryLecturesByQueryInfinite({ filters: { speaker_id: id, library: 'true' }, pageSize: PAGE_SIZE }),
      { sideload: fetchLectureAccessPermissionsSideload(store), secondarySideload: fetchLectureStatistics(store) }
    );

    return {
      props: {
        fallback: store.fallback,
        accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
      },
    };
  } catch (err) {
    return {
      notFound: true,
    };
  }
};

export default SpeakerPage;
